import MultiSelect from '@thanx/uikit/select/MultiSelect'
import { Switch } from '@thanx/uikit/switch'
import { useStyletron } from '@thanx/uikit/theme'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useMemo } from 'react'
import { InputGroup } from 'react-bootstrap'

import CustomDropdown from './CustomDropdown'

import type { Option } from 'baseui/select'
import type { Fields as Pagination } from 'models/Pagination'

const DEFAULT_VALUE_NAME = 'item'

type EnhancedMultiSelectProps = {
  options: Option[]
  value: Option[]
  valueName?: string
  onChange: (values: Option[]) => void
  onSearch?: (search: string) => void
  onPageChange?: (page: number) => void
  pagination?: Pagination | undefined
  placeholder?: string
  isLoading?: boolean
  selectAllLabel?: string
  onFocus?: () => void
  onBlur?: () => void
  maxDropdownHeight?: string
  labelKey?: string
  valueKey?: string
  isFocused?: boolean
  disabled?: boolean
  getCustomLabel?: (option?: Option) => React.ReactNode
}

const EnhancedMultiSelect = ({
  options,
  value,
  onChange,
  onSearch,
  onPageChange,
  pagination,
  isLoading = false,
  isFocused = false,
  getCustomLabel,
  valueKey,
  valueName = DEFAULT_VALUE_NAME,
  ...rest
}: EnhancedMultiSelectProps) => {
  const [css] = useStyletron()
  const [selectAll, setSelectAll] = useState(false)
  const isTest = process.env.NODE_ENV === 'test'
  const containerRef = useRef<HTMLDivElement>(null)

  const paginatedOptions = useMemo(() => {
    if (!pagination) return options

    return options.filter(option => {
      const currentPage = pagination?.current_page || 1
      const key = valueKey || 'id'
      return pagination.record_ids?.[currentPage]?.includes(option[key])
    })
  }, [options, pagination, valueKey])

  const setInitialSelectAll = useCallback(() => {
    const allVisibleSelected =
      paginatedOptions.length > 0 &&
      paginatedOptions.every(option =>
        value.some(
          selectedOption => selectedOption.chain_uid === option.chain_uid
        )
      )
    setSelectAll(allVisibleSelected)
  }, [paginatedOptions, value])

  // We need to replace the word 'item' in the placeholder override with 'valueName', but
  // there are currently a number of dependency issues with thanx-uikit that prevent us
  // from leveraging the built in functionality at the moment - this will be resolved once
  // thanx-uikit is updated to 5.2.1
  const updatePlaceholderOverrides = useCallback(() => {
    if (!containerRef.current || valueName === DEFAULT_VALUE_NAME) return
    const inputElements =
      containerRef.current.querySelectorAll<HTMLInputElement>(
        'input[placeholder]'
      )

    inputElements.forEach(input => {
      const { placeholder } = input
      if (placeholder.includes(DEFAULT_VALUE_NAME)) {
        input.placeholder = placeholder.replace(
          new RegExp(DEFAULT_VALUE_NAME, 'g'),
          valueName
        )
      }
    })
  }, [valueName])

  useEffect(() => {
    setInitialSelectAll()
    updatePlaceholderOverrides()
  }, [setInitialSelectAll, updatePlaceholderOverrides])

  const handleOnChange = (selectedOptions: Option[]) => {
    onChange(selectedOptions)
  }

  const handleSelectAllChange = (selected: boolean) => {
    setSelectAll(selected)
    if (selected) {
      onChange([...value, ...paginatedOptions])
    } else {
      // Deselect all options on the current page
      const filteredValues = value.filter(
        valueOption =>
          !paginatedOptions.some(
            option => option.chain_uid === valueOption.chain_uid
          )
      )
      onChange(filteredValues)
    }
  }

  const handleInputChange = (event: React.FormEvent<HTMLInputElement>) => {
    const searchValue = event.currentTarget.value
    onSearch?.(searchValue)
  }

  // Clear the search term on close
  const handleClose = () => {
    onSearch?.('')
  }

  const handleCustomLabel = (option?: Option) => {
    if (Boolean(getCustomLabel)) {
      return getCustomLabel?.(option)
    }
    return <>{option?.name || option?.label || ''}</>
  }

  // The custom dropdown override interferes with event propagation in the test environment,
  // preventing onFormChange from firing correctly. We disable it in tests to ensure
  // the core selection functionality works as expected.
  const multiSelectOverrides = isTest
    ? undefined
    : {
        Dropdown: {
          component: CustomDropdown,
          props: {
            pagination,
            onPageChange,
            selectAll,
            isLoading,
            value,
            onSelectAllChange: handleSelectAllChange,
            placement: 'bottom',
          },
        },
        // Ensure the dropdown always opens 'downward' by overriding Popover props
        Popover: {
          props: {
            placement: 'bottom',
            popperOptions: {
              modifiers: {
                flip: {
                  enabled: false,
                },
                preventOverflow: {
                  enabled: false,
                },
              },
            },
          },
        },
      }

  return (
    <div ref={containerRef} className={css({ width: '100%' })}>
      <div className="d-flex flex-row">
        <Switch condition={isFocused}>
          <InputGroup.Addon className={css({ width: '46px', height: '48px' })}>
            <i className="fa fa-search pt-xxs"></i>
          </InputGroup.Addon>
        </Switch>
        <MultiSelect
          options={paginatedOptions || options}
          onChange={handleOnChange}
          value={value}
          isLoading={isLoading}
          onInputChange={handleInputChange}
          onClose={handleClose}
          overrides={multiSelectOverrides}
          getOptionLabel={({ option }) => handleCustomLabel(option)}
          closeOnSelect={false}
          valueKey={valueKey}
          {...rest}
        />
      </div>
    </div>
  )
}

export default EnhancedMultiSelect
